/**
 * Data layer for `log-level-data-access`. Thin typed fetch wrappers over /api/v1/log-level-data-access.
 * Log-level audit trail: every access event emitted by the platform, filterable + exportable.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type AuditResult = "allow" | "warn" | "deny";
export type AuditResourceType =
	| "campaign"
	| "tracking_link"
	| "conversion"
	| "payout"
	| "invoice"
	| "user"
	| "publisher"
	| "advertiser"
	| "api_key"
	| "report";

export interface AuditLogRow {
	id: string;
	tenant_id: string;
	ts: number;
	user_id: string;
	user_email: string | null;
	role: string | null;
	resource_type: AuditResourceType | string;
	resource_id: string;
	action: string;
	result: AuditResult;
	ip: string | null;
	user_agent: string | null;
	created_at: number;
	updated_at: number;
}

export interface AuditLogFilters {
	user_id?: string;
	resource_type?: string;
	action?: string;
	result?: AuditResult;
	from?: number;
	to?: number;
	limit?: number;
}

export interface ListAuditLogResponse {
	count: number;
	items: AuditLogRow[];
	users: Array<{ id: string; email: string | null }>;
	resource_types: string[];
	actions: string[];
}

export interface LogLevelDataAccessRow extends AuditLogRow {
	[key: string]: unknown;
}

export interface ListLogLevelDataAccessResponse extends ListAuditLogResponse {}

export interface CreateLogLevelDataAccessBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/log-level-data-access";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function qs(filters: AuditLogFilters): string {
	const p = new URLSearchParams();
	if (filters.user_id) p.set("user_id", filters.user_id);
	if (filters.resource_type) p.set("resource_type", filters.resource_type);
	if (filters.action) p.set("action", filters.action);
	if (filters.result) p.set("result", filters.result);
	if (filters.from != null) p.set("from", String(filters.from));
	if (filters.to != null) p.set("to", String(filters.to));
	if (filters.limit != null) p.set("limit", String(filters.limit));
	const s = p.toString();
	return s ? `?${s}` : "";
}

export async function list(
	filters: AuditLogFilters = {},
): Promise<ListLogLevelDataAccessResponse> {
	const res = await fetch(`${BASE}${qs(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLogLevelDataAccessResponse>(res);
}

export async function create(body: CreateLogLevelDataAccessBody): Promise<LogLevelDataAccessRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<LogLevelDataAccessRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export async function exportCsv(filters: AuditLogFilters = {}): Promise<string> {
	const res = await fetch(`${BASE}/export.csv${qs(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "text/csv" },
	});
	if (!res.ok) {
		const text = await res.text();
		throw new Error(text || `HTTP ${res.status}`);
	}
	return res.text();
}

export const logLevelDataAccessQueryKeys = {
	all: ["log-level-data-access"] as const,
	list: (filters: AuditLogFilters = {}) =>
		[...["log-level-data-access"], "list", filters] as const,
	detail: (id: string) => [...["log-level-data-access"], id] as const,
};
